/**
 * 话题编辑弹窗：加话题 / 改话题名（P2-2）。
 *
 * 加话题可以顺手带若干道题（一行一题），并选「放在哪个话题后面」；不选则接在最后。
 * 话题名、题面、数量上限与 content-pipeline 一致，后端会二次校验。
 */
import { useEffect } from 'react';
import { Form, Input, Modal, Select, Typography } from 'antd';
import {
  MAX_QUESTIONS_PER_NEW_TOPIC,
  QUESTION_TEXT_MAX_LENGTH,
  TOPIC_TITLE_MAX_LENGTH,
} from '../../../api/advisor';

const { TextArea } = Input;
const { Text } = Typography;

export type TopicEditorState =
  | { mode: 'add' }
  | { mode: 'rename'; topicId: string; title: string }
  | null;

export interface TopicEditorValues {
  title: string;
  afterTopicId?: string;
  /** 一行一题（仅加话题） */
  questions?: string;
}

/** 一行一题 → 题目数组（去空行、去首尾空白） */
export function splitQuestionLines(raw: string | undefined): string[] {
  return (raw ?? '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

export function TopicEditorModal({
  state,
  topics,
  loading,
  onCancel,
  onSubmit,
}: {
  state: TopicEditorState;
  topics: Array<{ topicId: string; title: string }>;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (values: TopicEditorValues) => void;
}) {
  const [form] = Form.useForm<TopicEditorValues>();

  // 弹窗内容打开后才挂载，setFieldsValue 会落空；每次打开按状态重置
  useEffect(() => {
    if (state) form.setFieldsValue({ title: state.mode === 'rename' ? state.title : '', afterTopicId: undefined, questions: '' });
  }, [state, form]);

  return (
    <Modal
      title={state?.mode === 'rename' ? '改话题名' : '加一个话题'}
      open={!!state}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okText="保存"
      confirmLoading={loading}
      destroyOnHidden
    >
      <Form
        name="topic_editor"
        key={state ? (state.mode === 'rename' ? `rename-${state.topicId}` : 'add') : 'closed'}
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{ title: state?.mode === 'rename' ? state.title : '' }}
        onFinish={onSubmit}
      >
        <Form.Item
          name="title"
          label="话题名"
          rules={[
            { required: true, whitespace: true, message: '请填写话题名' },
            { max: TOPIC_TITLE_MAX_LENGTH, message: `最多 ${TOPIC_TITLE_MAX_LENGTH} 个字` },
          ]}
        >
          <Input maxLength={TOPIC_TITLE_MAX_LENGTH} showCount placeholder="如：纺织厂的岁月" />
        </Form.Item>

        {state?.mode === 'add' && (
          <>
            <Form.Item name="afterTopicId" label="放在哪个话题后面" extra="不选则接在最后">
              <Select
                allowClear
                placeholder="接在最后"
                options={topics.map((t) => ({ value: t.topicId, label: t.title }))}
              />
            </Form.Item>
            <Form.Item
              name="questions"
              label="问题（一行一题，可以先不填）"
              extra={`最多 ${MAX_QUESTIONS_PER_NEW_TOPIC} 题，每题不超过 ${QUESTION_TEXT_MAX_LENGTH} 字；一道题只问一件事，口语、具体。`}
              rules={[
                {
                  validator: async (_rule, value: string | undefined) => {
                    const lines = splitQuestionLines(value);
                    if (lines.length > MAX_QUESTIONS_PER_NEW_TOPIC) {
                      throw new Error(`最多 ${MAX_QUESTIONS_PER_NEW_TOPIC} 题`);
                    }
                    if (lines.some((l) => l.length > QUESTION_TEXT_MAX_LENGTH)) {
                      throw new Error(`每题不超过 ${QUESTION_TEXT_MAX_LENGTH} 字`);
                    }
                  },
                },
              ]}
            >
              <TextArea autoSize={{ minRows: 3, maxRows: 8 }} placeholder={'第一次进厂是哪一年？\n师傅是谁，怎么带你的？'} />
            </Form.Item>
            <Text type="secondary">新话题里的题叙叙会照常问；已聊过的内容不会受影响。</Text>
          </>
        )}
      </Form>
    </Modal>
  );
}
